import { ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface ExpandableChildrenProps {
    maxHeightClass?: string;
    className?: string;
    children: ReactNode;
}

export function ExpandableChildren({
    maxHeightClass,
    className,
    children,
}: ExpandableChildrenProps) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    useEffect(() => {
        if (!maxHeightClass) return;
        const element = scrollRef.current;
        if (!element) return;

        const update = () => {
            setCanScrollUp(element.scrollTop > 0);
            setCanScrollDown(
                element.scrollTop + element.clientHeight <
                    element.scrollHeight - 1,
            );
        };

        update();
        element.addEventListener('scroll', update, { passive: true });
        const observer = new ResizeObserver(update);
        observer.observe(element);
        Array.from(element.children).forEach((child) =>
            observer.observe(child),
        );

        return () => {
            element.removeEventListener('scroll', update);
            observer.disconnect();
        };
    }, [maxHeightClass, children]);

    if (!maxHeightClass) {
        return (
            <div className={cn('flex flex-col pl-3', className)}>
                {children}
            </div>
        );
    }

    return (
        <div
            ref={scrollRef}
            className={cn(
                'expandable-fade-mask flex flex-col overflow-y-auto pl-3 scrollbar-hide',
                maxHeightClass,
                className,
            )}
            data-fade-top={canScrollUp}
            data-fade-bottom={canScrollDown}
        >
            {children}
        </div>
    );
}
